import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Fab,
  Grid,
  Typography,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SelectEntity from "components/SelectEntity";
import SelectResource from "components/SelectResource";
import SelectResourceTypeMultiple from "components/SelectResourceTypeMultiple";
import SelectSku from "components/SelectSku";
import SelectSkuAvailability from "components/SelectSkuAvailability";
import SelectUserAgreementTypeMultiple from "components/SelectUserAgreementTypeMultiple";
import SnackbarMessage from "components/SnackbarMessage";
import { endOfDay, isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  IdNameModel,
  ListRequestOfSkuUserAgreementFilter,
  ListResponseOfIdNameModel,
  ResourceV2ListItem,
  SkuUserAgreementFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import { convertToUTC } from "utils/dateUtils";
import DateRangeSelect from "./DateRangeSelect";
import UserAgreementStatusView from "./UserAgreementStatusView";

const UserAgreementList = () => {
  const { formatMessage: t } = useIntl();
  const {
    skuUserAgreementService,
    skuUserAgreementDetailsUrl,
    skuUserAgreementCreateUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const agreementsResponse = useQuery<ListResponseOfIdNameModel>(
    ["skuUserAgreementService.getSkuUserAgreementAgreements", organizationId],
    () => skuUserAgreementService.getSkuUserAgreementAgreements()
  );
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("sku.sku");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [skuId, setSkuId] = useState<string | null>(null);
  const [skuAvailabilityId, setSkuAvailabilityId] = useState<string>();
  const [faculty, setFaculty] = useState<ResourceV2ListItem | null>(null);
  const [resourceTypeIds, setResourceTypeIds] = useState<string[]>([]);
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [dueDateFrom, setDueDateFrom] = useState<Date | null>(null);
  const [dueDateTo, setDueDateTo] = useState<Date | null>(null);
  const [statusIds, setStatusIds] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  const { skuIdParam, statusIdParam, organizationUserIdParam } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      skuIdParam: params.get("skuId"),
      statusIdParam: params.get("statusId"),
      organizationUserIdParam: params.get("userId"),
    };
  }, [location.search]);

  useEffect(() => {
    skuIdParam && setSkuId(skuIdParam);
  }, [skuIdParam]);

  useEffect(() => {
    statusIdParam && setStatusIds([statusIdParam]);
  }, [statusIdParam]);

  useEffect(() => {
    organizationUserIdParam &&
      setFaculty({
        organizationUserId: organizationUserIdParam,
      } as ResourceV2ListItem);
  }, [organizationUserIdParam]);

  useListTitle("userAgreements");

  const { data, isFetching } = useQuery(
    [
      "getSkuUserAgreements",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      skuId,
      skuAvailabilityId,
      faculty?.organizationUserId,
      resourceTypeIds,
      agreementId,
      dueDateFrom,
      dueDateTo,
      statusIds,
    ],
    () =>
      skuUserAgreementService.getSkuUserAgreements(
        ListRequestOfSkuUserAgreementFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: SkuUserAgreementFilter.fromJS({
            skuId: skuId,
            skuAvailabilityId,
            organizationUserId: faculty?.organizationUserId,
            resourceTypeIds,
            agreementId,
            dueDateFrom:
              dueDateFrom == null || !isValid(dueDateFrom)
                ? null
                : convertToUTC(dueDateFrom),
            dueDateTo:
              dueDateTo == null || !isValid(dueDateTo)
                ? null
                : convertToUTC(endOfDay(dueDateTo)),
            statusIds,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const onClick = (id: string) => {
    navigate(skuUserAgreementDetailsUrl(id));
  };

  useEffect(() => {
    setPage(0);
  }, [
    organizationId,
    skuId,
    skuAvailabilityId,
    faculty?.organizationUserId,
    resourceTypeIds,
    agreementId,
    dueDateFrom,
    dueDateTo,
    statusIds,
  ]);

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      type: "text",
      label: t({ id: "skuLabel" }),
      customSortAccessor: "sku.sku",
    },
    {
      accessor: "skuTitle",
      type: "text",
      label: t({ id: "skuTitleColumnLabel" }),
      customSortAccessor: "sku.title",
    },
    {
      accessor: "skuAvailabilityName",
      type: "text",
      label: t({ id: "availabilityLabel" }),
      customSortAccessor: "sku.skuAvailability.name",
    },
    {
      accessor: "userName",
      type: "text",
      label: t({ id: "userNameLabel" }),
      noSort: true,
    },
    {
      accessor: "resourceTypeNames",
      type: "text",
      label: t({ id: "facultyRoles" }),
      noSort: true,
    },
    {
      accessor: "agreementName",
      type: "text",
      label: t({ id: "agreement" }),
    },
    {
      accessor: "dueDate",
      type: "date",
      label: t({ id: "dueDate" }),
    },
    {
      accessor: "statusName",
      type: "node",
      width: 1,
      label: t({ id: "statusLabel" }),
      customSortAccessor: "status.name",
      View: UserAgreementStatusView,
    },
  ];

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link
          to={skuUserAgreementCreateUrl()}
          style={{ textDecoration: "none" }}
        >
          <Fab aria-label="add" color="primary">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="filtersLabel" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <SelectSku
                fullWidth
                value={skuId}
                onChange={setSkuId}
                organizationId={organizationId}
                label={t({ id: "skuLabel" })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectSkuAvailability
                fullWidth
                label={t({ id: "skuAvailabilityLabel" })}
                value={skuAvailabilityId}
                onChange={setSkuAvailabilityId}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectResource
                value={faculty}
                onChange={setFaculty}
                variant="outlined"
                filter={{ hasAnySkuUserAgreement: true }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <SelectResourceTypeMultiple
                onChange={setResourceTypeIds}
                value={resourceTypeIds}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectEntity
                value={agreementId ?? ""}
                fetchedData={agreementsResponse}
                labelField="name"
                label={t({ id: "agreement" })}
                onChange={(id) => setAgreementId(id ?? null)}
                renderOption={(props, option: IdNameModel) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                textFieldProps={{ variant: "outlined" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateRangeSelect
                label={t({ id: "dueDate" })}
                value={{
                  from: dueDateFrom,
                  to: dueDateTo,
                }}
                onChange={(from, to) => {
                  setDueDateFrom(from);
                  setDueDateTo(to);
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectUserAgreementTypeMultiple
                value={statusIds}
                onChange={setStatusIds}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: data?.data ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "userAgreements" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default UserAgreementList;
